import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Users, Briefcase, User, Phone, Check, ArrowRight, Building2, MapPin, FileText } from 'lucide-react';

export default function Recrutement() {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';
  const { API } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState('employeur');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [employeurData, setEmployeurData] = useState({
    nom: '', entreprise: '', telephone: '', email: '', type_poste: '',
    localisation_poste: '', description_poste: '', date_demarrage: '', type_contrat: '', rgpd_accepted: false,
  });
  
  const [candidatData, setCandidatData] = useState({
    nom: '', prenom: '', telephone: '', email: '', type_poste_recherche: '',
    annees_experience: '', mobilite_geographique: '', rgpd_accepted: false,
  });

  const typesPostes = [
    { value: 'operateur_foreuse', label: isFr ? 'Opérateur de foreuse' : 'Drill operator' },
    { value: 'aide_foreur', label: isFr ? 'Aide-foreur' : 'Drilling assistant' },
    { value: 'mecanicien', label: isFr ? 'Mécanicien TP / Engins' : 'Heavy equipment mechanic' },
    { value: 'technicien_labo', label: isFr ? 'Technicien laboratoire' : 'Lab technician' },
    { value: 'technicien_terrain', label: isFr ? 'Technicien terrain' : 'Field technician' },
    { value: 'ingenieur_geotechnicien', label: isFr ? 'Ingénieur géotechnicien' : 'Geotechnical engineer' },
    { value: 'chef_chantier', label: isFr ? 'Chef de chantier' : 'Site manager' },
    { value: 'commercial', label: isFr ? 'Commercial / Technico-commercial' : 'Sales engineer' },
    { value: 'autre', label: isFr ? 'Autre' : 'Other' },
  ];

  const typesContrats = [
    { value: 'CDI', label: 'CDI' },
    { value: 'CDD', label: 'CDD' },
    { value: 'Interim', label: isFr ? 'Intérim' : 'Temporary' },
    { value: 'Stage', label: isFr ? 'Stage' : 'Internship' },
    { value: 'Alternance', label: isFr ? 'Alternance' : 'Apprenticeship' },
  ];

  const handleSubmitEmployeur = async (e) => {
    e.preventDefault();
    if (!employeurData.rgpd_accepted) { toast.error(isFr ? 'Acceptez les conditions' : 'Accept terms'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.keys(employeurData).forEach(k => { if (employeurData[k] !== '' && employeurData[k] !== null) fd.append(k, employeurData[k]); });
      await axios.post(`${API}/demandes/recrutement/employeur`, fd);
      setSubmitted(true);
    } catch (error) { toast.error(isFr ? 'Erreur' : 'Error'); }
    finally { setLoading(false); }
  };

  const handleSubmitCandidat = async (e) => {
    e.preventDefault();
    if (!candidatData.rgpd_accepted) { toast.error(isFr ? 'Acceptez les conditions' : 'Accept terms'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.keys(candidatData).forEach(k => { if (candidatData[k] !== '' && candidatData[k] !== null) fd.append(k, candidatData[k]); });
      await axios.post(`${API}/demandes/recrutement/candidat`, fd);
      setSubmitted(true);
    } catch (error) { toast.error(isFr ? 'Erreur' : 'Error'); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><Check className="w-10 h-10 text-green-600" /></div>
          <h1 className="text-3xl font-bold text-[#0A2540] mb-4">{isFr ? 'Merci !' : 'Thank you!'}</h1>
          <p className="text-gray-600 mb-6">{isFr ? 'Votre demande a été enregistrée. Nous vous recontactons rapidement.' : 'Your request has been registered. We\'ll contact you soon.'}</p>
          <div className="flex items-center justify-center gap-2 text-[#635BFF] font-medium"><Phone className="w-5 h-5" /><span>+33 7 82 93 93 83</span></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="pt-16 pb-8 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-rose-100 rounded-full px-4 py-2 mb-6">
            <Users className="w-4 h-4 text-rose-600" />
            <span className="text-sm font-medium text-rose-600">{isFr ? 'Recrutement géotechnique' : 'Geotechnical recruitment'}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-4">{isFr ? 'Personnel qualifié géotech' : 'Qualified geotech staff'}</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{isFr ? 'Opérateurs, techniciens, ingénieurs... Trouvez les meilleurs talents du secteur.' : 'Operators, technicians, engineers... Find the best talents in the industry.'}</p>
        </div>
      </section>

      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-2xl">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="employeur" className="flex items-center gap-2 data-[state=active]:bg-rose-600 data-[state=active]:text-white">
                <Briefcase className="w-4 h-4" />{isFr ? 'Je recrute' : 'I\'m hiring'}
              </TabsTrigger>
              <TabsTrigger value="candidat" className="flex items-center gap-2 data-[state=active]:bg-rose-600 data-[state=active]:text-white">
                <User className="w-4 h-4" />{isFr ? 'Je cherche un emploi' : 'I\'m looking for a job'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="employeur">
              <form onSubmit={handleSubmitEmployeur} className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label>{isFr ? 'Nom complet' : 'Full name'} *</Label><Input required value={employeurData.nom} onChange={(e) => setEmployeurData({ ...employeurData, nom: e.target.value })} className="mt-2" /></div>
                    <div><Label>{isFr ? 'Entreprise' : 'Company'} *</Label><Input required value={employeurData.entreprise} onChange={(e) => setEmployeurData({ ...employeurData, entreprise: e.target.value })} className="mt-2" /></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label>{isFr ? 'Téléphone' : 'Phone'} *</Label><Input type="tel" required value={employeurData.telephone} onChange={(e) => setEmployeurData({ ...employeurData, telephone: e.target.value })} className="mt-2" /></div>
                    <div><Label>Email</Label><Input type="email" value={employeurData.email} onChange={(e) => setEmployeurData({ ...employeurData, email: e.target.value })} className="mt-2" /></div>
                  </div>
                  <div>
                    <Label>{isFr ? 'Type de poste' : 'Position type'} *</Label>
                    <Select value={employeurData.type_poste} onValueChange={(v) => setEmployeurData({ ...employeurData, type_poste: v })}>
                      <SelectTrigger className="mt-2"><SelectValue placeholder={isFr ? 'Sélectionnez' : 'Select'} /></SelectTrigger>
                      <SelectContent>{typesPostes.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>{isFr ? 'Localisation du poste' : 'Job location'} *</Label><Input required value={employeurData.localisation_poste} onChange={(e) => setEmployeurData({ ...employeurData, localisation_poste: e.target.value })} placeholder="Lyon, France" className="mt-2" /></div>
                  <div><Label>{isFr ? 'Description du poste' : 'Job description'} *</Label><Textarea required value={employeurData.description_poste} onChange={(e) => setEmployeurData({ ...employeurData, description_poste: e.target.value })} placeholder={isFr ? 'Missions, compétences requises...' : 'Tasks, required skills...'} className="mt-2" rows={3} /></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label>{isFr ? 'Date de démarrage' : 'Start date'} *</Label><Input required value={employeurData.date_demarrage} onChange={(e) => setEmployeurData({ ...employeurData, date_demarrage: e.target.value })} placeholder={isFr ? 'Ex: ASAP / Juin 2025' : 'Ex: ASAP / June 2025'} className="mt-2" /></div>
                    <div>
                      <Label>{isFr ? 'Type de contrat' : 'Contract type'} *</Label>
                      <Select value={employeurData.type_contrat} onValueChange={(v) => setEmployeurData({ ...employeurData, type_contrat: v })}>
                        <SelectTrigger className="mt-2"><SelectValue placeholder={isFr ? 'Sélectionnez' : 'Select'} /></SelectTrigger>
                        <SelectContent>{typesContrats.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="pt-4 border-t">
                    <div className="flex items-start gap-3">
                      <Checkbox id="rgpd-emp" checked={employeurData.rgpd_accepted} onCheckedChange={(c) => setEmployeurData({ ...employeurData, rgpd_accepted: c })} />
                      <Label htmlFor="rgpd-emp" className="text-sm text-gray-600 cursor-pointer">{isFr ? "J'accepte le traitement de mes données (RGPD)" : "I accept data processing (GDPR)"}</Label>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading || !employeurData.rgpd_accepted} className="w-full bg-rose-600 hover:bg-rose-700 text-white py-4 text-lg">
                    {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <span className="flex items-center gap-2">{isFr ? 'Trouver un candidat' : 'Find a candidate'}<ArrowRight className="w-5 h-5" /></span>}
                  </Button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="candidat">
              <form onSubmit={handleSubmitCandidat} className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label>{isFr ? 'Prénom' : 'First name'} *</Label><Input required value={candidatData.prenom} onChange={(e) => setCandidatData({ ...candidatData, prenom: e.target.value })} className="mt-2" /></div>
                    <div><Label>{isFr ? 'Nom' : 'Last name'} *</Label><Input required value={candidatData.nom} onChange={(e) => setCandidatData({ ...candidatData, nom: e.target.value })} className="mt-2" /></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label>{isFr ? 'Téléphone' : 'Phone'} *</Label><Input type="tel" required value={candidatData.telephone} onChange={(e) => setCandidatData({ ...candidatData, telephone: e.target.value })} className="mt-2" /></div>
                    <div><Label>Email *</Label><Input type="email" required value={candidatData.email} onChange={(e) => setCandidatData({ ...candidatData, email: e.target.value })} className="mt-2" /></div>
                  </div>
                  <div>
                    <Label>{isFr ? 'Type de poste recherché' : 'Position sought'} *</Label>
                    <Select value={candidatData.type_poste_recherche} onValueChange={(v) => setCandidatData({ ...candidatData, type_poste_recherche: v })}>
                      <SelectTrigger className="mt-2"><SelectValue placeholder={isFr ? 'Sélectionnez' : 'Select'} /></SelectTrigger>
                      <SelectContent>{typesPostes.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label>{isFr ? "Années d'expérience" : 'Years of experience'} *</Label><Input type="number" required value={candidatData.annees_experience} onChange={(e) => setCandidatData({ ...candidatData, annees_experience: e.target.value })} placeholder="5" className="mt-2" /></div>
                    <div><Label>{isFr ? 'Mobilité géographique' : 'Geographic mobility'} *</Label><Input required value={candidatData.mobilite_geographique} onChange={(e) => setCandidatData({ ...candidatData, mobilite_geographique: e.target.value })} placeholder={isFr ? 'Ex: Rhône-Alpes / France entière' : 'Ex: Nationwide'} className="mt-2" /></div>
                  </div>
                  <div className="pt-4 border-t">
                    <div className="flex items-start gap-3">
                      <Checkbox id="rgpd-cand" checked={candidatData.rgpd_accepted} onCheckedChange={(c) => setCandidatData({ ...candidatData, rgpd_accepted: c })} />
                      <Label htmlFor="rgpd-cand" className="text-sm text-gray-600 cursor-pointer">{isFr ? "J'accepte le traitement de mes données (RGPD)" : "I accept data processing (GDPR)"}</Label>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading || !candidatData.rgpd_accepted} className="w-full bg-rose-600 hover:bg-rose-700 text-white py-4 text-lg">
                    {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <span className="flex items-center gap-2">{isFr ? 'Publier mon profil' : 'Publish my profile'}<ArrowRight className="w-5 h-5" /></span>}
                  </Button>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
